import React, { useState } from "react";
import {
  Typography,
  Box,
  Grid2,
  styled,
  useTheme,
  Skeleton,
} from "@mui/material";
import Highcharts from "highcharts/highstock";
import PieChart from "highcharts-react-official";
import { useGetPendingByAuthorityQuery } from "store/slices/apis/keyClearancesDashboardSlice";
import { useParams } from "react-router-dom";
import ClearancePopup from "./ClearancePopup";

const PendingClearancesGraph = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useGetPendingByAuthorityQuery({ projectId });

  const authorityData = data?.data || [];
  const predefinedColors = [
    "#FF5733",
    "#33B5FF",
    "#FF33D4",
    "#33FFBD",
    "#FFC300",
    "#8E44AD",
    "#2ECC71",
    "#E67E22",
    "#3498DB",
  ];

  const pieData = authorityData.map((item, idx) => ({
    y: item?.count,
    value: item?.percentage,
    color: predefinedColors[idx % predefinedColors.length],
    approvingAuthority: item?.approvingAuthority,
  }));


  const options = {
    chart: {
      type: "pie",
      width: 210,
      height: 190,
      backgroundColor: "transparent",
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        return `<b>${this.point.approvingAuthority}</b>: ${this.y}`;
      },
      style: {
        fontSize: "13px",
        fontWeight: "500",
        color: "#000",
      },
      backgroundColor: "#ffffff",
      borderColor: "#ccc",
      borderRadius: 4,
      shadow: true,
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
          format: "{(point.y)}%",
          style: {
            fontSize: "13px",
            fontWeight: "600",
            lineHeight: "20px",
            letterSpacing: "0.5px",
            color: "#fff",
            fill: "#fff",
          },
          distance: -20,
        },
        showInLegend: false,
        innerSize: 90,
        startAngle: 50,
        borderRadius: 0,
        borderWidth: 0,
        point: {
          events: {
            click: function () {
              setPopupData(this?.approvingAuthority);
              setOpen(true);
            },
          },
        },
      },
    },
    series: [
      {
        name: "",
        color: "#006600",
        lineWidth: 1,
        marker: {
          enabled: false,
          symbol: "circle",
          radius: 3,
          states: {
            hover: {
              enabled: true,
              lineWidth: 1,
            },
          },
        },
        data: pieData,
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        marginTop: "10px",
        border: "1px solid #D8D8D8",
        borderRadius: "12px",
        pb: "0px",
      }}
    >
      <Grid2 container>
        <Box sx={{ p: 1.5, pb: 0 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: `${theme?.customSettings?.themeColor}`,
              fontWeight: "500",
              fontSize: "18px",
              //paddingTop: "20px",
              lineHeight: "100%",
              letterSpacing: "0.4px",
            }}
          >
            Clearances Pending by Approving Authority
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: "#182A4E80", whiteSpace: "nowrap" }}
          >
             List of clearances pending at Authority level
          </Typography>
        </Box>
      </Grid2>

      <Box
        sx={{ maxHeight: "190px", overflowY: "auto", pr: 1, display: "flex" }}
      >
        <Grid2 size={{ xs: 12, md: 7 }}>
          {isLoading ? (
            [...Array(3)].map((_, idx) => (
              <Grid2
                container
                direction="row"
                spacing={1}
                key={idx}
                style={{ marginLeft: "10px", paddingTop: "15px" }}
              >
                <Skeleton
                  variant="rectangular"
                  width={18}
                  height={18}
                  sx={{ marginTop: "6px" }}
                />
                <Skeleton
                  variant="text"
                  width={100}
                  height={28}
                  sx={{ marginLeft: 2 }}
                />
                <Skeleton
                  variant="text"
                  width={30}
                  height={28}
                  sx={{ marginLeft: 2 }}
                />
              </Grid2>
            ))
          ) : !isLoading && authorityData.length === 0 ? (
            <Typography
              sx={{
                padding: 2,
                fontSize: "14px",
                color: "#999",
                whiteSpace: "nowrap",
              }}
            >
              No data available to display the chart.
            </Typography>
          ) : (
            authorityData?.map((item, idx) => (
              <Grid2
                container
                direction="row"
                spacing={1}
                key={idx}
                style={{ marginLeft: "10px", paddingTop: "0px" }}
              >
                <Typography style={{ padding: "8px", width: "25px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "18px",
                      height: "18px",
                      borderRadius: "0",
                      backgroundColor: pieData[idx]?.color || "#ccc",
                    }}
                  ></span>
                </Typography>
                <Typography
                  style={{
                    padding: "8px",
                    width: "140px",
                    color: "#000000",
                    fontSize: "13px",
                    fontWeight: "500",
                    opacity: "0.5",
                  }}
                >
                  {item?.approvingAuthority}
                </Typography>
                {/* <Typography
                  style={{
                    padding: "4px",
                    width: "30px",
                    color: "#415385",
                    fontSize: "20px",
                    fontWeight: "700",
                  }}
                >
                  {item?.count}
                </Typography> */}
              </Grid2>
            ))
          )}
        </Grid2>
        <Grid2
          size={{ xs: 12, md: 5 }}
          sx={{
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
          }}
        >
          <Typography style={{ marginLeft: "-40px" }}>
            {isLoading ? (
              <Skeleton
                variant="circular"
                width={180}
                height={180}
                sx={{ mb: 3, ml: 4 }}
              />
            ) : (
              <PieChart highcharts={Highcharts} options={options} />
            )}
          </Typography>
        </Grid2>
      </Box>

      {open && (
        <ClearancePopup
          title="Clearances Pending by Approving Authority"
          value={popupData}
          status="ApprovingAuthority"
          onOpen={open}
          onClose={() => setOpen(false)}
        />
      )}
    </Box>
  );
};

export default PendingClearancesGraph;
